import React from "react";
import PropTypes from "prop-types";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import ContractorForm from "./ContractorForm";
import TablePageReport from "../../../../components/TablePageReport";
import {
  COMPANY_STATUS_IDS,
  COMPANY_STATUS_LABELS,
  TableEmptyMessage,
  addressToString,
  getCompanyDeleteText,
  isCompanyActivated,
  isCompanyDeactivated,
} from "../../../../components/Utils";
import SalesTableUtil from "../../../../components/table/SalesTableUtil";

const getState = (model) => {
  const items = model || [];
  return {
    items: items.map((item, index) => ({ ...item, index })),
    error: null,
    first: 0,
    filter: {
      status_id: [],
    },
  };
};

function ContractorListForm({
  forwardedRef,
  className = "",
  model = [],
  rows = 10,
  getBaseData,
  exportSubCompanies,
  companies,
  disabled: _disabled,
  readOnly,
  isExportable,
}) {
  const disabled = readOnly || _disabled;
  const dataTableRef = React.useRef(null);
  const formRef = React.useRef(null);
  const [state, setState] = React.useState(getState(model));

  const validate = React.useCallback(() => {
    const { items } = state;
    return [...items];
  }, [state]);

  const getModel = React.useCallback(
    (noValidate) => validate(noValidate),
    [validate],
  );
  React.useImperativeHandle(forwardedRef, () => ({ getModel }));

  // const toggleAddition = (disabled) => setState(prev => ({ ...prev, addDisabled: disabled }));
  // const toggleError = (error) => setState(prev => ({ ...prev, error }));

  const onSave = React.useCallback(
    (onHide) => {
      const newItem = formRef.current.getModel();
      console.log(">> saving contractor ...", newItem);
      if (!newItem) return;
      const items = state.items;
      if (newItem.index !== undefined && items[newItem.index] !== undefined) {
        items[newItem.index] = newItem;
      } else {
        newItem.index = items.length;
        items.push(newItem);
      }
      setState((prev) => ({ ...prev, items }));
      onHide && onHide();
    },
    [state, formRef.current],
  );

  const onDelete = React.useCallback(
    (selected) => {
      const items = [];
      state.items.forEach((item) => {
        if (item.name !== selected.name) {
          items.push(item);
        }
      });
      setState((prev) => ({ ...prev, items }));
    },
    [state],
  );

  const onDeActivate = React.useCallback(
    (selected, isActivated) => {
      const items = [];
      state.items.forEach((item) => {
        if (item.name == selected.name) {
          item.status_id = isActivated
            ? COMPANY_STATUS_IDS.ACTIVATED
            : COMPANY_STATUS_IDS.DEACTIVATED;
        }
        items.push(item);
      });
      console.log("onDeActivate, selected", selected, items);
      setState((prev) => ({ ...prev, items }));
    },
    [state],
  );

  const onExport = React.useCallback(() => {
    const baseData = getBaseData ? getBaseData() : {};
    const company = baseData.getCompany ? baseData.getCompany() : {};
    console.log("onExport()", company);
    exportSubCompanies({ companyId: company.id });
  }, [getBaseData, exportSubCompanies]);

  const onFilterChange = React.useCallback((field, value) => {
    console.log("onFilterChange, field, value", field, value);
    setState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        [field]: value,
      },
    }));
  }, []);

  // const { items: contractors, filter } = state;
  const filterKeys = React.useMemo(() => Object.keys(state.filter), [state]);

  const onPage = React.useCallback(
    (event) => setState((prev) => ({ ...prev, first: event.first })),
    [],
  );
  const contractorList = companies.filter((item) => {
    const found = state.items.find((contractor) => contractor.id === item.id);
    return !found;
  });

  const useMultiSelectFilter = React.useMemo(() => {
    const status_id = [
      {
        label: COMPANY_STATUS_LABELS[COMPANY_STATUS_IDS.ACTIVATED],
        value: COMPANY_STATUS_IDS.ACTIVATED,
      },
      {
        label: COMPANY_STATUS_LABELS[COMPANY_STATUS_IDS.DEACTIVATED],
        value: COMPANY_STATUS_IDS.DEACTIVATED,
      },
    ];
    const optionMap = { status_id };
    return SalesTableUtil.createUseMultiSelectFilter(
      dataTableRef.current,
      state.items,
      optionMap,
      onFilterChange,
      state.filter.status_id,
    );
  }, [state, dataTableRef.current, onFilterChange]);
  const statusFilter = useMultiSelectFilter("status_id");

  const renderItemTable = React.useCallback(
    (items, actionTemplate) => {
      const data = items
        .map((item) => {
          const address = Array.isArray(item.address)
            ? item.address[0]
            : item.address;
          return {
            ...item,
            address,
            addressStr: addressToString(address),
            status_id: item.status_id || COMPANY_STATUS_IDS.ACTIVATED,
            status:
              item.status ||
              COMPANY_STATUS_LABELS[
                item.status_id || COMPANY_STATUS_IDS.ACTIVATED
              ],
          };
        })
        .filter((item) => {
          const isValid = [];
          for (const key of filterKeys) {
            if (
              Array.isArray(state.filter[key]) &&
              state.filter[key].length > 0
            ) {
              isValid.push(state.filter[key].includes(item[key]));
            }
          }
          if (isValid.length > 0) {
            for (const valid of isValid) {
              if (!valid) {
                return false;
              }
            }
          }
          return true;
        });
      return (
        <DataTable
          ref={dataTableRef}
          value={data}
          rows={rows}
          paginator={data.length > rows}
          first={state.first}
          onPage={onPage}
          paginatorLeft={
            <TablePageReport
              className="pad-lft"
              totalRecords={data.length}
              filteredRecords={data.length}
              first={state.first}
              rows={rows}
              emptyText="Keine Subunternehmen"
              itemName="Subunternehmen"
              itemNamePlural="Subunternehmen"
            />
          }
          emptyMessage={
            <TableEmptyMessage itemNamePlural="Subunternehmen" min />
          }
        >
          <Column
            field="schulungsportalId"
            header="ID Schulungsportal"
            sortable={data.length > 1}
            filter
            filterMatchMode="contains"
            style={{ width: "12em" }}
          />
          <Column
            field="name"
            header="Firma"
            sortable={data.length > 1}
            filter
            filterMatchMode="contains"
          />
          <Column
            field="addressStr"
            header="Adresse"
            sortable={data.length > 1}
            filter
            filterMatchMode="contains"
          />
          <Column
            field="activeEmployees" // "totalEmployees"
            header="Aktive Mitarbeiter"
            sortable={data.length > 1}
            style={{ textAlign: "center", width: "8em" }}
          />
          <Column
            field="status_id"
            header="Status"
            sortable={data.length > 1}
            filter
            {...statusFilter}
            body={(rowData) => rowData.status}
            style={{ textAlign: "center", width: "9em" }}
          />
          {
            <Column
              body={actionTemplate}
              style={{ textAlign: "center", width: "10em" }}
            />
          }
        </DataTable>
      );
    },
    [rows, state, onPage, filterKeys],
  );

  const renderItemForm = React.useCallback(
    (item, onHide) => {
      const contractor = item || {
        index: state.items.length,
        name: "",
        type_id: 4,
        groups: [],
      };
      const baseData = getBaseData ? getBaseData() : {};
      return (
        <div>
          <ContractorForm
            ref={formRef}
            key={Math.random()}
            model={contractor}
            companies={contractorList}
            disabled={disabled}
            {...baseData}
          />
          <FormMenu
            className="margin-big-top w3-border-top pad-big w3-light-grey"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            onSave={() => onSave(onHide)}
            onCancel={onHide}
            saveBtnName="saveContractorBtn"
            saveHidden={disabled}
            cancelBtnName="cancelContractorBtn"
            cancelBtnLabel={disabled ? "Schließen" : undefined}
          />
        </div>
      );
    },
    [state, contractorList, getBaseData, disabled, onSave, formRef.current],
  );

  const getItemFormTitle = React.useCallback((contractor) => {
    let title = "hinzufügen";
    if (contractor) {
      title = disabled ? "anzeigen" : "bearbeiten";
    }
    return <h3 className="no-margin">{`Subunternehmen ${title}`}</h3>;
  }, []);

  const titleClassName = `no-margin-top`;
  return (
    <ItemListManager
      key={Math.random()}
      className={className}
      title={<h3 className={titleClassName}>{"Subunternehmen"}</h3>}
      addBtnLabel="Hinzufügen"
      addBtnName="addContractorBtn"
      addBtnHidden={disabled}
      exportBtnName="exportContractorsBtn"
      exportBtnDisabled={state.items.length === 0}
      exportBtnHidden={!isExportable}
      onExport={() => onExport()}
      items={state.items}
      getItemFormTitle={getItemFormTitle}
      renderItemTable={renderItemTable}
      renderItemForm={renderItemForm}
      namePrefix="contractor"
      itemDeleteable={!disabled}
      isDeleteDisabled={(item) => !isCompanyDeactivated(item)}
      deleteTitle="Subunternehmen löschen"
      getDeleteText={(item) => getCompanyDeleteText(item, true)}
      onDelete={onDelete}
      itemDeactivateable={!disabled}
      showDisabledDeActivate
      activateTitle="Subunternehmen aktivieren"
      getActivateText={(item) =>
        `Wollen Sie das Subunternehmen ${item.name} wirklich aktivieren?`
      }
      isActivateDisabled={(item) => isCompanyActivated(item)}
      deactivateTitle="Subunternehmen deaktivieren"
      getDeactivateText={(item) =>
        `Wollen Sie das Subunternehmen ${item.name} wirklich deaktivieren?`
      }
      isDeactivateDisabled={(item) =>
        !isCompanyActivated(item) || item.activeEmployees > 0
      }
      onDeActivate={onDeActivate}
    />
  );
}

ContractorListForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.array,
  companies: PropTypes.array,
  getBaseData: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  rows: PropTypes.number,
  exportSubCompanies: PropTypes.func.isRequired,
  isExportable: PropTypes.bool,
};

export default React.forwardRef((props, ref) => (
  <ContractorListForm {...props} forwardedRef={ref} />
));
